/**
 * React hook for license state. Fetches on mount + on window focus + on
 * interval; `pollUntilActive` opt polls aggressively for a short window
 * after post-checkout return.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchLicenses } from './licenses';
import { type LicenseSummary } from './types';

export type LicenseHookState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'missing' }
  | { status: 'active'; license: LicenseSummary }
  | { status: 'past_due'; license: LicenseSummary }
  | { status: 'expired'; license: LicenseSummary }
  | { status: 'revoked'; license: LicenseSummary };

export interface UseLicenseOptions {
  /**
   * When true, polls every `pollIntervalMs` (default 1500) until a
   * terminal state (active | past_due) is reached or `pollTimeoutMs`
   * (default 30000) elapses. Intended for `/billing/return` flows.
   */
  pollUntilActive?: boolean;
  pollIntervalMs?: number;
  pollTimeoutMs?: number;
  /** Refetch on window focus + periodic cadence (default 5min). */
  refreshInBackground?: boolean;
  backgroundIntervalMs?: number;
  /**
   * When false, the hook is inert — useful for routes that should gate on
   * auth state (pass `enabled: ready && !!user`).
   */
  enabled?: boolean;
}

const DEFAULT_POLL_INTERVAL = 1500;
const DEFAULT_POLL_TIMEOUT = 30000;
const DEFAULT_BACKGROUND_INTERVAL = 5 * 60 * 1000;

function summarize(summary: LicenseSummary | undefined): LicenseHookState {
  if (!summary) return { status: 'missing' };
  switch (summary.status) {
    case 'active':
      return { status: 'active', license: summary };
    case 'past_due':
      return { status: 'past_due', license: summary };
    case 'expired':
      return { status: 'expired', license: summary };
    case 'revoked':
      return { status: 'revoked', license: summary };
    default:
      return { status: 'error', message: `Unknown license status: ${String(summary.status)}` };
  }
}

function isTerminal(state: LicenseHookState): boolean {
  return state.status === 'active' || state.status === 'past_due';
}

export function useLicense(
  productId: string,
  options: UseLicenseOptions = {}
): LicenseHookState & { refresh: () => Promise<void> } {
  const [state, setState] = useState<LicenseHookState>({ status: 'idle' });
  const cancelledRef = useRef(false);

  const enabled = options.enabled !== false;
  const pollUntilActive = options.pollUntilActive === true;
  const pollIntervalMs = options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL;
  const pollTimeoutMs = options.pollTimeoutMs ?? DEFAULT_POLL_TIMEOUT;
  const refreshInBackground = options.refreshInBackground !== false;
  const backgroundIntervalMs = options.backgroundIntervalMs ?? DEFAULT_BACKGROUND_INTERVAL;

  const fetchOnce = useCallback(async (): Promise<LicenseHookState> => {
    try {
      const { licenses } = await fetchLicenses();
      return summarize(licenses[productId]);
    } catch (err) {
      return {
        status: 'error',
        message: err instanceof Error ? err.message : 'License fetch failed',
      };
    }
  }, [productId]);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setState((prev) => (prev.status === 'loading' ? prev : { status: 'loading' }));
    const next = await fetchOnce();
    if (!cancelledRef.current) setState(next);
  }, [enabled, fetchOnce]);

  useEffect(() => {
    cancelledRef.current = false;
    if (!enabled) {
      setState({ status: 'idle' });
      return () => {
        cancelledRef.current = true;
      };
    }

    setState({ status: 'loading' });

    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    let pollDeadline = 0;
    let backgroundTimer: ReturnType<typeof setInterval> | null = null;

    const doFetch = async (): Promise<LicenseHookState> => {
      const next = await fetchOnce();
      if (!cancelledRef.current) setState(next);
      return next;
    };

    const schedulePoll = (): void => {
      if (cancelledRef.current) return;
      if (Date.now() >= pollDeadline) return;
      pollTimer = setTimeout(async () => {
        const next = await doFetch();
        if (isTerminal(next)) return;
        schedulePoll();
      }, pollIntervalMs);
    };

    void (async () => {
      const initial = await doFetch();
      if (pollUntilActive && !isTerminal(initial)) {
        pollDeadline = Date.now() + pollTimeoutMs;
        schedulePoll();
      }
    })();

    const onFocus = (): void => {
      void doFetch();
    };
    if (refreshInBackground && typeof window !== 'undefined') {
      window.addEventListener('focus', onFocus);
      backgroundTimer = setInterval(() => void doFetch(), backgroundIntervalMs);
    }

    return () => {
      cancelledRef.current = true;
      if (pollTimer) clearTimeout(pollTimer);
      if (backgroundTimer) clearInterval(backgroundTimer);
      if (refreshInBackground && typeof window !== 'undefined') {
        window.removeEventListener('focus', onFocus);
      }
    };
  }, [
    enabled,
    productId,
    pollUntilActive,
    pollIntervalMs,
    pollTimeoutMs,
    refreshInBackground,
    backgroundIntervalMs,
    fetchOnce,
  ]);

  return { ...state, refresh };
}

/**
 * Short copy for paywall/upgrade UI, returns `null` when nothing useful
 * to show. Consumers can wrap this or write their own; kept here because
 * it's a natural companion to the hook.
 */
const RENEW_SOON_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

function formatExpiryDate(expiresAt: string | null | undefined): string | null {
  if (!expiresAt) return null;
  const d = new Date(expiresAt);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

export function formatLicenseHint(state: LicenseHookState, _productId: string): string | null {
  switch (state.status) {
    case 'idle':
      return null;
    case 'loading':
      return null;
    case 'missing':
      return null;
    case 'active': {
      const renewOn = formatExpiryDate(state.license.expiresAt);
      const expiresAt = state.license.expiresAt ? new Date(state.license.expiresAt).getTime() : NaN;
      const soon =
        !Number.isNaN(expiresAt) &&
        expiresAt - Date.now() >= 0 &&
        expiresAt - Date.now() <= RENEW_SOON_WINDOW_MS;
      if (soon && renewOn) return `Your license renews on ${renewOn}.`;
      return 'A license is active. If the app has not unlocked, click "Refresh license" to sync it.';
    }
    case 'past_due': {
      const byDate = formatExpiryDate(state.license.expiresAt);
      if (byDate) {
        return `Your subscription payment didn't go through. You can keep using the app during the grace period — please update your payment method before ${byDate} to avoid interruption.`;
      }
      return 'Your subscription is past due. You can keep using the app during the grace period; please update your payment method.';
    }
    case 'expired': {
      const endedOn = formatExpiryDate(state.license.expiresAt);
      if (endedOn) return `Your license expired on ${endedOn}. Purchase a new plan to continue.`;
      return 'Your license has expired. Purchase a new plan to continue.';
    }
    case 'revoked':
      return 'This license has been revoked.';
    case 'error': {
      const msg = state.message;
      if (/network\s*error/i.test(msg) || /failed\s+to\s+fetch/i.test(msg)) {
        return 'Network Error';
      }
      return `Could not read license data: ${msg}`;
    }
    default:
      return null;
  }
}
